// Shared result-row for the Cmd-K palette and the /mi/search route.
// Renders the same indicator chips the lead-list / AOI / pool-order
// surfaces use so a search hit reads identically to those rows.
//
// active=true gives the row keyboard-focused styling (highlighted bg
// + accent border). The palette toggles this with arrow keys; the
// route page never sets it.

import { Link } from 'react-router-dom';
import type { PartySearchResult } from '../services/partyApi';
import { PushedIndicator } from './PushedIndicator';
import { LeadListIndicator } from './LeadListIndicator';
import { SkipMarker } from './SkipMarker';
import { SkipTraceOutcomeIndicators } from './SkipTraceOutcomeIndicators';
import { HasContactIndicator } from './HasContactIndicator';

const entityChipClass = (type: string | null | undefined): string => {
  if (!type) return 'bg-text-faint/15 text-text-faint';
  if (['llc', 'corp', 'partnership'].includes(type)) return 'bg-amber/15 text-amber';
  if (type === 'trust') return 'bg-blue/15 text-blue';
  if (type === 'individual') return 'bg-green/15 text-green';
  return 'bg-text-faint/15 text-text-faint';
};

interface Props {
  result: PartySearchResult;
  active?: boolean;
  /** Called on click. When omitted, the row navigates via React
   *  Router. Palette sets this to close itself + navigate. */
  onSelect?: () => void;
}

export function SearchResultRow({ result: r, active = false, onSelect }: Props) {
  const cls =
    'block rounded px-3 py-2 border ' +
    (active
      ? 'border-accent/60 bg-accent-soft text-text'
      : 'border-transparent hover:bg-bg-hover/40 hover:border-border text-text');

  const content = (
    <>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[13px] truncate">
          {r.display_name_natural ?? r.display_name}
        </span>
        <PushedIndicator pushedAt={r.last_pushed_to_close_at} />
        <LeadListIndicator status={r.lead_list_status} />
        <SkipMarker skippedAt={r.skipped_at} />
        <SkipTraceOutcomeIndicators
          deceased={r.deceased}
          needsReview={r.needs_review}
          skipTracedAt={r.skip_traced_at}
        />
        <span className={'shrink-0 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider rounded ' + entityChipClass(r.entity_type)}>
          {r.entity_type}
        </span>
        <HasContactIndicator hasPhone={r.has_phone} hasAddress={r.has_address} className="shrink-0" />
        {r.on_lead_list && (
          <span className="shrink-0 text-[9px] font-mono uppercase tracking-wider px-1 py-0 rounded border border-amber/40 bg-amber/10 text-amber">
            on list
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-text-faint truncate">
        <span className="truncate">{r.normalized_name}</span>
        {(r.skip_trace_city || r.skip_trace_state) && (
          <>
            <span className="text-text-ghost">·</span>
            <span className="text-text-dim">{[r.skip_trace_city, r.skip_trace_state].filter(Boolean).join(', ')}</span>
          </>
        )}
        <span className="ml-auto text-text-ghost">{r.via}</span>
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button onClick={onSelect} className={cls + ' w-full text-left'}>
        {content}
      </button>
    );
  }
  return (
    <Link to={`/mi/party/${r.party_id}`} className={cls}>
      {content}
    </Link>
  );
}
